import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Calendar,
  Users,
  Ticket,
  DollarSign,
  Clock,
  MapPin,
  Edit,
  Trash2,
  Check,
  X,
  AlertCircle,
  TrendingUp,
  Link as LinkIcon,
  Copy,
  Search,
  Filter,
  Download,
  MoreVertical,
  Sparkles,
} from "lucide-react";
import qs from "qs";
import { useAppSelector } from "@/store/hooks";
import { selectEventFilters } from "@/store/features/events/eventsSlice";
import { useParams } from "react-router-dom";
import { useGetNestedsQuery } from "@/store/features/nested/nestedAPI";
import { useToast } from "@/hooks/use-toast";

// Sample Event Data
const eventData = {
  id: "MTX2025",
  name: "Mumbai Tech Summit 2025",
  date: "2025-12-15T10:00:00",
  venue: "NSCI Dome, Mumbai",
  status: "active",
  ticketsSold: 487,
  totalCapacity: 500,
  revenue: 731000,
  projectedRevenue: 750000,
  subdomain: "mumbaitech.aboutblank.app",
  customDomain: "techsummit2025.com",
};

// Refund Requests
const refundRequests = [
  {
    id: "RF-1024",
    guestName: "Karan Mehta",
    bookingId: "MTX2025-4591",
    ticketType: "VIP",
    amount: 2500,
    reason: "Schedule Conflict",
    status: "pending",
    requestDate: "2025-11-18",
  },
  {
    id: "RF-1023",
    guestName: "Neha Singh",
    bookingId: "MTX2025-4592",
    ticketType: "Regular",
    amount: 1500,
    reason: "Personal Emergency",
    status: "approved",
    requestDate: "2025-11-17",
  },
  {
    id: "RF-1022",
    guestName: "Vikram Joshi",
    bookingId: "MTX2025-4593",
    ticketType: "Early Bird",
    amount: 1200,
    reason: "Event Canceled",
    status: "processed",
    requestDate: "2025-11-16",
  },
];

// Countdown Timer Component
function CountdownTimer({ targetDate }: { targetDate: string }) {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = new Date(targetDate).getTime() - new Date().getTime();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div className="flex gap-3 flex-wrap justify-center sm:justify-start">
      {[
        { label: "Days", value: timeLeft.days },
        { label: "Hours", value: timeLeft.hours },
        { label: "Minutes", value: timeLeft.minutes },
        { label: "Seconds", value: timeLeft.seconds },
      ].map((item) => (
        <div
          key={item.label}
          className="bg-gradient-to-br from-theme-primary/10 to-theme-secondary/10 backdrop-blur-sm border border-border/40 rounded-xl p-3 text-center min-w-[70px] hover:scale-105 transition-transform duration-300"
        >
          <div className="text-2xl font-bold bg-gradient-to-r from-theme-primary to-theme-secondary bg-clip-text text-transparent">
            {item.value}
          </div>
          <div className="text-xs text-muted-foreground mt-1">{item.label}</div>
        </div>
      ))}
    </div>
  );
}

export default function GuestList() {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [isSubdomainDialogOpen, setIsSubdomainDialogOpen] = useState(false);
  const [customSubdomain, setCustomSubdomain] = useState("");
  const [selectedRefund, setSelectedRefund] = useState<any>(null);
  const [isRefundDialogOpen, setIsRefundDialogOpen] = useState(false);
  const filters = useAppSelector(selectEventFilters);
  const { eventId } = useParams<{ eventId: string }>();
  const { toast } = useToast();

  // ✅ Build dynamic query from Redux filters
  const query = useMemo(() => {
    const queryConfig: any = {
      populate: {
        event: {
          fields: ["title", "start_date", "total_revenue", "registered_count", "capacity", "slug","view_count"],
        },
        attendee: {
          fields: ["name", "email"],
        },
       
      },
      pagination: {
        page: filters.page,
        pageSize: filters.limit,
      },
    };

    if (eventId) {
      queryConfig.filters = {
        ...queryConfig.filters,
        event: {
          documentId: {
            $eq: eventId,
          },
        },
      };
    }

    return qs.stringify(queryConfig, { encodeValuesOnly: true });
  }, [filters, eventId]);

  let collectionName = "booking";
  let mappedcollectionname = "organization";
  let mappedcollectionKey = "owner";
  let path = `${collectionName}/${mappedcollectionname}/${mappedcollectionKey}?${query}`;
  const { data, isLoading, isFetching } = useGetNestedsQuery(path);

  // Guest List Data
  const guestListData = data?.data || [];

  const filteredGuests = guestListData.filter((guest) => {
    const matchesSearch =
      guest?.attendee?.name?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
      guest?.attendee?.email?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
      guest?.bookingId?.toLowerCase().includes(searchQuery?.toLowerCase());
    const matchesFilter = filterStatus === "all" || guest.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const eventDetails = guestListData[0]?.event;
  const registeredCount = eventDetails?.registered_count || 0;
  const capacity = eventDetails?.capacity || 0;
  const capacityPercentage = capacity > 0 ? (registeredCount / capacity) * 100 : 0;

  useEffect(() => {
    setCustomSubdomain(eventDetails?.slug || "");
  }, [eventDetails]);

  const handleCopySubdomain = () => {
    navigator.clipboard.writeText(`https://aboutblank.app/event/${customSubdomain}`);
    toast({
      title: "Copied!",
      description: "Subdomain link copied to clipboard",
    });
  };

  const handleRefundAction = (refund: any, action: string) => {
    setSelectedRefund({ ...refund, action });
    setIsRefundDialogOpen(true);
  };

  

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute top-[-10%] right-[-5%] w-[800px] h-[800px] rounded-full blur-[120px] opacity-20 animate-pulse"
          style={{
            background:
              "radial-gradient(circle, rgb(var(--theme-primary) / 0.3) 0%, rgb(var(--theme-secondary) / 0.2) 50%, transparent 100%)",
            animationDuration: "8s",
          }}
        />
        <div
          className="absolute bottom-[-15%] left-[-5%] w-[700px] h-[700px] rounded-full blur-[100px] opacity-15 animate-pulse"
          style={{
            background:
              "radial-gradient(circle, rgb(var(--theme-accent) / 0.3) 0%, rgb(var(--theme-secondary) / 0.2) 50%, transparent 100%)",
            animationDuration: "10s",
            animationDelay: "2s",
          }}
        />
      </div>

      <div className="relative z-10 p-4 sm:p-6 space-y-6 max-w-7xl mx-auto">
        {/* Event Overview Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-theme-primary/10 via-pink-500/10 to-theme-secondary/10 border border-theme-primary/20 backdrop-blur-sm">
              <Sparkles className="w-3.5 h-3.5 text-theme-primary-500" />
              <span className="text-xs font-semibold">Event Management</span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-foreground via-foreground/80 to-foreground/60 bg-clip-text text-transparent">
              {eventDetails?.title || "Event Overview"}
            </h1>

            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-theme-primary-500" />
                {eventDetails?.start_date
                  ? new Date(eventDetails.start_date).toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })
                  : "Date TBD"}
              </span>
              <span className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-pink-500" />
                {eventData.venue}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Badge className="bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30 backdrop-blur-sm">
              {eventData.status?.toUpperCase()}
            </Badge>
            <Button
              variant="outline"
              className="gap-2 backdrop-blur-sm bg-card/40 border-border/40 hover:bg-accent hover:border-theme-primary/40 transition-all"
            >
              <Edit className="h-4 w-4" />
              Edit Event
            </Button>
          </div>
        </div>

        {/* Countdown & Quick Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="bg-card/40 backdrop-blur-xl border-border/40 hover:border-theme-primary/40 transition-all duration-500 overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-theme-primary/5 via-transparent to-theme-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            <CardHeader className="relative">
              <CardTitle className="flex items-center gap-2 text-foreground">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20">
                  <Clock className="h-5 w-5 text-purple-500" />
                </div>
                Event Countdown
              </CardTitle>
            </CardHeader>
            <CardContent className="relative">
              <CountdownTimer targetDate={eventDetails?.start_date || eventData.date} />
            </CardContent>
          </Card>

          <Card className="bg-card/40 backdrop-blur-xl border-border/40 hover:border-theme-primary/40 transition-all duration-500 overflow-hidden group">
            <div className="absolute inset-0 bg-gradient-to-br from-theme-primary/5 via-transparent to-theme-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            <CardHeader className="relative">
              <CardTitle className="flex items-center gap-2 text-foreground">
                <div className="p-2 rounded-lg bg-gradient-to-br from-pink-500/20 to-orange-500/20">
                  <Ticket className="h-5 w-5 text-pink-500" />
                </div>
                Ticket Sales Progress
              </CardTitle>
            </CardHeader>
            <CardContent className="relative space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground text-sm">Tickets Sold</span>
                <span className="text-foreground font-bold text-xl">
                  {registeredCount} / {capacity}
                </span>
              </div>
              <div className="w-full bg-muted/30 rounded-full h-3 overflow-hidden border border-border/20">
                <div
                  className="bg-gradient-to-r from-purple-500 via-pink-500 to-theme-primary h-3 rounded-full transition-all duration-700 relative overflow-hidden"
                  style={{ width: `${Math.min(capacityPercentage, 100)}%` }}
                >
                  <div className="absolute inset-0 bg-white/20 animate-pulse" />
                </div>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-purple-500 dark:text-purple-400 font-medium">
                  {capacityPercentage.toFixed(1)}% Capacity
                </span>
                <span className="text-pink-500 dark:text-pink-400 font-medium">
                  {capacity - registeredCount} Remaining
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Analytics Overview Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              title: "Total Revenue",
              value: `₹${eventData.revenue.toLocaleString()}`,
              subtitle: `${((eventData.revenue / eventData.projectedRevenue) * 100).toFixed(1)}% of goal`,
              icon: DollarSign,
              gradient: "from-purple-500 to-pink-500",
              bgGradient: "from-purple-500/20 to-pink-500/20",
            },
            {
              title: "Total Guests",
              value: eventData.ticketsSold,
              subtitle: "Registered attendees",
              icon: Users,
              gradient: "from-pink-500 to-orange-500",
              bgGradient: "from-pink-500/20 to-orange-500/20",
            },
            {
              title: "Avg Ticket",
              value: `₹${Math.round(eventData.revenue / eventData.ticketsSold)}`,
              subtitle: "Per ticket average",
              icon: TrendingUp,
              gradient: "from-theme-primary to-theme-secondary",
              bgGradient: "from-theme-primary/20 to-theme-secondary/20",
            },
            {
              title: "Pending Refunds",
              value: refundRequests.filter((r) => r.status === "pending").length,
              subtitle: "Requires action",
              icon: AlertCircle,
              gradient: "from-orange-500 to-red-500",
              bgGradient: "from-orange-500/20 to-red-500/20",
            },
          ].map((stat, idx) => (
            <Card
              key={idx}
              className="bg-card/40 backdrop-blur-xl border-border/40 hover:border-theme-primary/40 hover:shadow-lg hover:shadow-theme-primary/10 transition-all duration-500 hover:-translate-y-1 overflow-hidden group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-theme-primary/5 via-transparent to-theme-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              <CardHeader className="flex flex-row items-center justify-between pb-2 relative">
                <CardTitle className="text-sm font-medium text-muted-foreground">{stat.title}</CardTitle>
                <div className={`p-2 rounded-lg bg-gradient-to-br ${stat.bgGradient}`}>
                  <stat.icon className={`h-4 w-4 bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`} />
                </div>
              </CardHeader>
              <CardContent className="relative">
                <div className={`text-2xl font-bold bg-gradient-to-r ${stat.gradient} bg-clip-text text-transparent`}>
                  {stat.value}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{stat.subtitle}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tabbed Content */}
        <Tabs defaultValue="guests" className="space-y-6">
          <TabsList className="bg-card/60 backdrop-blur-sm border border-border/40 p-1">
            <TabsTrigger
              value="guests"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-theme-primary data-[state=active]:to-theme-secondary data-[state=active]:text-white gap-2"
            >
              <Users className="h-4 w-4" />
              Guest List
            </TabsTrigger>
            <TabsTrigger
              value="refunds"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-theme-primary data-[state=active]:to-theme-secondary data-[state=active]:text-white gap-2"
            >
              <DollarSign className="h-4 w-4" />
              Refunds
            </TabsTrigger>
            <TabsTrigger
              value="subdomain"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-theme-primary data-[state=active]:to-theme-secondary data-[state=active]:text-white gap-2"
            >
              <LinkIcon className="h-4 w-4" />
              Subdomain
            </TabsTrigger>
          </TabsList>

          {/* Guest List Management Tab */}
          <TabsContent value="guests" className="space-y-4">
            <Card className="bg-card/40 backdrop-blur-xl border-border/40 overflow-hidden">
              <CardHeader>
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <Users className="h-5 w-5 text-theme-primary-500" />
                    Guest List Management
                  </CardTitle>
                  <Button className="bg-gradient-to-r from-theme-primary to-theme-secondary hover:shadow-lg hover:shadow-theme-primary/50 transition-all gap-2">
                    <Download className="h-4 w-4" />
                    Export CSV
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 mt-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name, email, or booking ID..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-background/60 backdrop-blur-sm border-border/40 focus:border-theme-primary/50"
                    />
                  </div>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-full sm:w-[180px] bg-background/60 backdrop-blur-sm border-border/40">
                      <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4" />
                        <SelectValue />
                      </div>
                    </SelectTrigger>
                    <SelectContent className="bg-popover backdrop-blur-xl border-border">
                      <SelectItem value="all">All Guests</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>

              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border/40 bg-muted/30">
                        <th className="text-left py-4 px-4 text-muted-foreground font-semibold text-xs uppercase tracking-wider">
                          Guest Name
                        </th>
                        <th className="text-left py-4 px-4 text-muted-foreground font-semibold text-xs uppercase tracking-wider">
                          Booking ID
                        </th>
                        <th className="text-left py-4 px-4 text-muted-foreground font-semibold text-xs uppercase tracking-wider">
                          Ticket Type
                        </th>
                        <th className="text-left py-4 px-4 text-muted-foreground font-semibold text-xs uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="text-left py-4 px-4 text-muted-foreground font-semibold text-xs uppercase tracking-wider">
                          Status
                        </th>
                        <th className="text-left py-4 px-4 text-muted-foreground font-semibold text-xs uppercase tracking-wider">
                          Check-in
                        </th>
                        <th className="text-left py-4 px-4 text-muted-foreground font-semibold text-xs uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredGuests.length > 0 ? (
                        filteredGuests.map((guest) => (
                          <tr
                            key={guest.id}
                            className="border-b border-border/20 hover:bg-muted/20 transition-colors"
                          >
                            <td className="py-4 px-4">
                              <div>
                                <p className="text-foreground font-medium">{guest.attendee?.name}</p>
                                <p className="text-muted-foreground text-xs mt-0.5">
                                  {guest.attendee?.email}
                                </p>
                              </div>
                            </td>
                            <td className="py-4 px-4 text-muted-foreground font-mono text-sm">
                              {guest.documentId}
                            </td>
                            <td className="py-4 px-4">
                              <Badge className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-600 dark:text-purple-400 border-purple-500/30">
                                {guest.ticketType || "Standard"}
                              </Badge>
                            </td>
                            <td className="py-4 px-4 text-foreground font-semibold">₹{guest.total}</td>
                            <td className="py-4 px-4">
                              <Badge
                                className={
                                  guest.payment_status === "paid"
                                    ? "bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30"
                                    : "bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-yellow-500/30"
                                }
                              >
                                {guest?.payment_status?.toUpperCase()}
                              </Badge>
                            </td>
                            <td className="py-4 px-4">
                              {guest.check_in_status ? (
                                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-green-500/20 border border-green-500/30">
                                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                                </div>
                              ) : (
                                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-muted/30 border border-border/30">
                                  <X className="h-4 w-4 text-muted-foreground" />
                                </div>
                              )}
                            </td>
                            <td className="py-4 px-4">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="hover:bg-accent transition-colors"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="py-12 text-center">
                            <div className="flex flex-col items-center gap-3">
                              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-theme-primary/20 to-theme-secondary/20 flex items-center justify-center">
                                <Search className="w-8 h-8 text-muted-foreground" />
                              </div>
                              <div className="space-y-1">
                                <p className="text-foreground font-medium">No guests found</p>
                                <p className="text-muted-foreground text-sm">
                                  Try adjusting your search or filters
                                </p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Refund Management Tab */}
          <TabsContent value="refunds" className="space-y-4">
            <Card className="bg-card/40 backdrop-blur-xl border-border/40 overflow-hidden">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-theme-primary-500" />
                  Refund Requests
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border/40 bg-muted/30">
                        {[
                          "Request ID",
                          "Guest Name",
                          "Booking ID",
                          "Amount",
                          "Reason",
                          "Status",
                          "Actions",
                        ].map((header) => (
                          <th
                            key={header}
                            className="text-left py-4 px-4 text-muted-foreground font-semibold text-xs uppercase tracking-wider"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {refundRequests.map((refund) => (
                        <tr
                          key={refund.id}
                          className="border-b border-border/20 hover:bg-muted/20 transition-colors"
                        >
                          <td className="py-4 px-4 text-theme-primary-500 font-mono text-sm font-medium">
                            {refund.id}
                          </td>
                          <td className="py-4 px-4 text-foreground font-medium">
                            {refund.guestName}
                          </td>
                          <td className="py-4 px-4 text-muted-foreground font-mono text-sm">
                            {refund.bookingId}
                          </td>
                          <td className="py-4 px-4 text-foreground font-semibold">
                            ₹{refund.amount}
                          </td>
                          <td className="py-4 px-4 text-muted-foreground">{refund.reason}</td>
                          <td className="py-4 px-4">
                            <Badge
                              className={
                                refund.status === "pending"
                                  ? "bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-yellow-500/30"
                                  : refund.status === "approved"
                                  ? "bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30"
                                  : "bg-blue-500/20 text-blue-600 dark:text-blue-400 border-blue-500/30"
                              }
                            >
                              {refund.status.toUpperCase()}
                            </Badge>
                          </td>
                          <td className="py-4 px-4">
                            {refund.status === "pending" && (
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => handleRefundAction(refund, "approve")}
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-red-500/50 text-red-600 dark:text-red-400 hover:bg-red-500/10"
                                  onClick={() => handleRefundAction(refund, "reject")}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Refund Action Dialog */}
            <Dialog open={isRefundDialogOpen} onOpenChange={setIsRefundDialogOpen}>
              <DialogContent className="bg-card/95 backdrop-blur-xl border-border">
                <DialogHeader>
                  <DialogTitle className="text-foreground">
                    {selectedRefund?.action === "approve" ? "Approve Refund" : "Reject Refund"}
                  </DialogTitle>
                  <DialogDescription className="text-muted-foreground">
                    {selectedRefund?.action === "approve"
                      ? "This will process the refund and return the amount to the customer."
                      : "This will reject the refund request."}
                  </DialogDescription>
                </DialogHeader>
                {selectedRefund && (
                  <div className="space-y-4">
                    <div className="bg-muted/30 backdrop-blur-sm rounded-lg p-4 space-y-3 border border-border/40">
                      {[
                        { label: "Guest Name", value: selectedRefund.guestName },
                        { label: "Refund Amount", value: `₹${selectedRefund.amount}` },
                        {
                          label: "Booking ID",
                          value: selectedRefund.bookingId,
                          mono: true,
                        },
                      ].map((item) => (
                        <div key={item.label} className="flex justify-between items-center">
                          <span className="text-muted-foreground text-sm">{item.label}:</span>
                          <span
                            className={`text-foreground font-medium ${
                              item.mono ? "font-mono text-sm" : ""
                            }`}
                          >
                            {item.value}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-3">
                      <Button
                        className={
                          selectedRefund.action === "approve"
                            ? "flex-1 bg-green-600 hover:bg-green-700 text-white"
                            : "flex-1 bg-red-600 hover:bg-red-700 text-white"
                        }
                        onClick={() => {
                          setIsRefundDialogOpen(false);
                          toast({
                            title: `Refund ${selectedRefund.action}d`,
                            description: `Request for ${selectedRefund.guestName} has been ${selectedRefund.action}d.`,
                          });
                        }}
                      >
                        Confirm {selectedRefund.action === "approve" ? "Approval" : "Rejection"}
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setIsRefundDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Subdomain Customization Tab */}
          <TabsContent value="subdomain" className="space-y-4">
            <Card className="bg-card/40 backdrop-blur-xl border-border/40 overflow-hidden">
              <CardHeader>
                <CardTitle className="text-foreground flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-pink-500/20">
                    <LinkIcon className="h-5 w-5 text-purple-500" />
                  </div>
                  Subdomain & Custom Domain
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-foreground font-medium mb-2 block">
                      Event Subdomain
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={customSubdomain}
                        onChange={(e) => setCustomSubdomain(e.target.value)}
                        className="flex-1 bg-background/60 backdrop-blur-sm border-border/40 focus:border-theme-primary/50"
                      />
                      <Button
                        variant="outline"
                        className="backdrop-blur-sm hover:bg-accent"
                        onClick={handleCopySubdomain}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-muted-foreground text-sm mt-2">
                      Your event page will be accessible at:{" "}
                      <span className="text-theme-primary-500 font-medium">
                       https://aboutblank.app/event/{customSubdomain}
                      </span>
                    </p>
                  </div>

                  <div>
                    <Label className="text-foreground font-medium mb-2 block">
                      Custom Domain (Optional)
                    </Label>
                    <Input
                      value={customSubdomain}
                      placeholder="yourdomain.com"
                      className="bg-background/60 backdrop-blur-sm border-border/40 focus:border-theme-primary/50"
                    />
                    <p className="text-muted-foreground text-sm mt-2">
                      Connect your own domain for a branded experience
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 backdrop-blur-sm rounded-xl p-4 border border-green-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-foreground font-medium flex items-center gap-2">
                        SSL Status
                        <span className="text-lg">🔒</span>
                      </span>
                      <Badge className="bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30">
                        Active
                      </Badge>
                    </div>
                    <p className="text-muted-foreground text-sm">
                      Your event page is secured with SSL encryption
                    </p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 backdrop-blur-sm rounded-xl p-4 border border-purple-500/20">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-foreground font-medium">Page Views</span>
                      <span className="text-transparent bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text font-bold text-lg">
                        2,080 total
                      </span>
                    </div>
                    <p className="text-muted-foreground text-sm">340 views this week</p>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button className="flex-1 bg-gradient-to-r from-theme-primary to-theme-secondary hover:shadow-lg hover:shadow-theme-primary/50 transition-all">
                    Save Changes
                  </Button>
                  <Button variant="outline" className="flex-1 backdrop-blur-sm hover:bg-accent">
                    Visit Event Page
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
